import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "202",
  "slug": "oil-gas-satellite-imagery-team-engagement",
  "title": "A Satellite Imagery Activity for an Energy Team",
  "excerpt": "A suggested group exercise using landforms and infrastructure as discussion prompts.",
  "tldr": "Energy companies use satellite-imagery games for team building because the format taps the same visual-interpretation skills geologists, exploration teams, and asset managers use day-to-day. It's a low-effort, low-cost activity that feels relevant to the work rather than like a generic icebreaker.",
  "date": "2026-03-07",
  "category": "Team Building",
  "image": "/blog-images/world.jpg",
  "readTime": "3 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Geologists, reservoir engineers, and field operations teams in the energy sector spend careers developing a detailed mental map of the world's terrain. They can look at a satellite image and identify the rock formation type, estimate the likely depth to pay zone, and name the basin — sometimes before they even consciously notice the landscape itself. That expertise is genuinely remarkable, and for years it has been invisible at company events dominated by generic trivia nights."
    },
    {
      "type": "paragraph",
      "text": "That's changing. A growing number of energy companies — particularly in oil and gas, mining, and renewables — are incorporating satellite imagery games into their team building programs, and the fit is surprisingly natural."
    },
    {
      "type": "heading",
      "text": "The Terrain Is Already Familiar"
    },
    {
      "type": "paragraph",
      "text": "Unlike most corporate teams, energy sector employees have a genuine and deep relationship with geography. Upstream teams can often identify producing basins from aerial views. Pipeline engineers recognize corridor geography. Offshore teams know platform locations and ocean shelf topography. When a satellite imagery game drops a bird's-eye view of the Permian Basin, the Athabasca Oil Sands, or the Norwegian Continental Shelf, these teams don't guess — they know. That creates a different kind of engagement than a pub trivia night ever could."
    },
    {
      "type": "heading",
      "text": "Cross-Functional Knowledge Gets Surfaced"
    },
    {
      "type": "paragraph",
      "text": "One of the persistent challenges in large energy companies is that technical departments become siloed. Drilling doesn't talk to logistics. Exploration doesn't know what production operations look like on the ground. A satellite imagery team challenge creates a context where someone from HSE knows the answer that stumped the geophysicist, and where a field technician's practical knowledge outscores the desk-bound analyst. That cross-functional leveling has real value beyond the game itself."
    },
    {
      "type": "heading",
      "text": "A suggested session for an energy team"
    },
    {
      "type": "paragraph",
      "text": "The most common format is a team-vs-team challenge using EarthGuessr's multiplayer mode, with teams of 4-6 people competing simultaneously. Some companies add a custom twist: they insert satellite images of their own operational sites and ask teams to identify them. Recognizing a company's own assets from the air creates an unexpectedly strong sense of pride and connection to the work."
    },
    {
      "type": "heading",
      "text": "Remote and Distributed Teams Benefit Most"
    },
    {
      "type": "paragraph",
      "text": "For teams in different time zones, collect responses to a prepared image asynchronously and share the explanation afterwards. A live multiplayer game still requires people to be available at the same time. Check account access and group size before choosing that format."
    },
    {
      "type": "list",
      "items": [
        "No app download or account required — runs in any browser",
        "Multiplayer supports distributed teams across locations and time zones",
        "Daily challenge format works as a recurring team ritual",
        "Scales from small department sessions to company-wide conferences",
        "Free to use — no per-seat licensing for basic features"
      ]
    }
  ],
  "imageAlt": "Earth regional satellite composite with shaded relief",
  "imageCaption": "NASA Earth Observatory / MODIS, August 2004. Regional composite with shaded relief and bathymetry; it shows landforms, not political boundaries or current conditions.",
  "sources": [
    {
      "title": "NASA: Blue Marble Next Generation (image source)",
      "url": "https://science.nasa.gov/earth/earth-observatory/blue-marble-next-generation/"
    }
  ]
};
